import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import deviceSensorsService from '../api/deviceSensorsService';
import type {
    DeviceSensor,
    CreateDeviceSensorDto,
    UpdateDeviceSensorDto,
} from '../types/types';

export const useDeviceSensors = () => {
    return useQuery<DeviceSensor[]>({
        queryKey: ['deviceSensors'],
        queryFn: () => deviceSensorsService.getAll(),
        refetchOnWindowFocus: false,
    });
};

export const useDeviceSensor = (id: number | undefined) => {
    return useQuery<DeviceSensor>({
        queryKey: ['deviceSensor', id],
        queryFn: () => deviceSensorsService.getById(id!),
        enabled: id != null,
        refetchOnWindowFocus: false,
    });
};

export const useCreateDeviceSensor = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: CreateDeviceSensorDto) => deviceSensorsService.create(dto),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['deviceSensors'] }),
    });
};

export const useUpdateDeviceSensor = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dto }: { id: number; dto: UpdateDeviceSensorDto }) =>
            deviceSensorsService.update(id, dto),
        onSuccess: (_, { id }) => {
            qc.invalidateQueries({ queryKey: ['deviceSensors'] });
            qc.invalidateQueries({ queryKey: ['deviceSensor', id] });
        },
    });
};

export const useDeleteDeviceSensor = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => deviceSensorsService.remove(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['deviceSensors'] }),
    });
};
